import React, { useState, useEffect, useRef } from 'react'
import useTalent from 'hooks/talent'
import { useParams } from 'react-router-dom'
import { Box, IconButton, Image, Text, Link, useMediaQuery, Modal, ModalContent, ModalOverlay, Button, HStack, LightMode, useColorModeValue, ModalCloseButton, ModalBody, Tooltip } from '@chakra-ui/react'
import { likesDisplay, getDatePart, getOptionLabel } from 'utils'
import { useSelector } from 'react-redux'
import { USER_TYPES } from 'constants'
import moment from 'moment'
import useStyle from 'hooks/style.'
import { API_URL } from 'constants'
import PalomaLogo from 'asset/logo.jpg'
import { FaCopy, FaFacebook, FaFilePdf, FaGlobe, FaInstagram, FaTiktok, FaImdb, FaYoutube, FaDownload } from 'react-icons/fa'
import TalentBookInfo from 'components/TalentBookInfo'
import { colors } from 'styles'
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'

const Icons = {
  facebook: <FaFacebook size={20} />,
  imdb: <FaImdb size={20} />,
  instagram: <FaInstagram size={20} />,
  personal: <FaGlobe size={20} />,
  tiktok: <FaTiktok size={20} />,
  youtube: <FaYoutube size={20} />
}

const HelperText = {
  facebook: 'Facebook',
  imdb: 'IMDB',
  instagram: 'Instagram',
  personal: 'Personal Website',
  tiktok: 'Tiktok',
  youtube: 'Youtube'
}

const socialKeys = ['facebook', 'tiktok', 'instagram', 'youtube', 'imdb', 'personal']

const TalentView = () => {
  const { slug } = useParams()
  const { talent, getTalentBySlugAndSet } = useTalent()
  const user = useSelector(state => state.user.currentUser)
  const enumData = useSelector(state => state.talent.enumData)
  const [showBookInfo, setShowBookInfo] = useState(false)
  const { darkColor } = useStyle()

  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(null);
  const [isPhotoModalOpen, setIsPhotoModalOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const printRef = useRef(null);

  const fetchAsDataUrl = async (url) => {
    const res = await fetch(url);
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const handleDownloadPdf = async () => {
    const node = printRef.current;
    if (!node) return;
    setIsDownloading(true);
    // Swap S3-redirect image URLs for same-origin proxied data URLs so
    // html2canvas can read pixel data without CORS tainting the canvas.
    const imgs = Array.from(node.querySelectorAll('img'));
    const originalSrcs = imgs.map(img => img.getAttribute('src'));
    await Promise.all(
      imgs.map(async (img, i) => {
        const src = originalSrcs[i];
        if (!src) return;
        const marker = '/s3/';
        const idx = src.indexOf(marker);
        if (idx === -1) return; // local bundled asset like the logo — leave alone
        const key = src.slice(idx + marker.length);
        const proxyUrl = `${API_URL}/s3-proxy/${key}`;
        try {
          const dataUrl = await fetchAsDataUrl(proxyUrl);
          img.setAttribute('src', dataUrl);
        } catch (err) {
          console.error('Failed to proxy image for PDF', src, err);
        }
      })
    );
    // Temporarily reveal the print-only node off-screen so html2canvas can capture it
    const prevDisplay = node.style.display;
    const prevPosition = node.style.position;
    const prevLeft = node.style.left;
    const prevTop = node.style.top;
    const prevWidth = node.style.width;
    const prevBackground = node.style.background;
    node.style.display = 'block';
    node.style.position = 'fixed';
    node.style.left = '-10000px';
    node.style.top = '0';
    node.style.width = '794px'; // ~A4 width at 96dpi
    node.style.background = '#ffffff';
    try {
      const canvas = await html2canvas(node, {
        useCORS: true,
        allowTaint: false,
        backgroundColor: '#ffffff',
        scale: 2
      });
      const imgData = canvas.toDataURL('image/jpeg', 0.95);
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;
      pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      pdf.save(`${name || 'talent'}.pdf`);
    } catch (err) {
      console.error('Failed to generate PDF', err);
    } finally {
      node.style.display = prevDisplay;
      node.style.position = prevPosition;
      node.style.left = prevLeft;
      node.style.top = prevTop;
      node.style.width = prevWidth;
      node.style.background = prevBackground;
      // Restore the original src attributes so the DOM is untouched.
      imgs.forEach((img, i) => {
        if (originalSrcs[i] != null) img.setAttribute('src', originalSrcs[i]);
      });
      setIsDownloading(false);
    }
  };

  const handlePhotoClick = (index) => {
    setSelectedPhotoIndex(index);
    setIsPhotoModalOpen(true);
  };

  const handlePhotoCloseModal = () => {
    setSelectedPhotoIndex(null);
    setIsPhotoModalOpen(false);
  };


  useEffect(() => {
    getTalentBySlugAndSet(slug)
  }, [slug])

  useEffect(() => {
  if (talent) {
    const name = talent.stage_first_name && talent.stage_last_name
      ? `${talent.stage_first_name} ${talent.stage_last_name}`
      : `${talent.first_name} ${talent.last_name}`;
    
    document.title = name;
  }
}, [talent]);

  if (!talent) { return <Text>Loading...</Text> }

  const name = talent?.stage_first_name && talent?.stage_last_name
  ? `${talent.stage_first_name} ${talent.stage_last_name}`
  : `${talent.first_name} ${talent.last_name}`;


  const talentAge = moment.duration(new Date() - new Date(talent?.birth_date))
  const ageText = talentAge?.asMonths() < 48
  ? `${Math.floor(talentAge?.asMonths())} months`
  : talentAge?.asYears() < 18
    ? `${Math.floor(talentAge.asYears())} years`
    : ''

  return (
    <Box>
      <Box className='hide-on-print' display='flex' flexDirection='column' h='100vh'>
        <Box px={5} display='flex' mt={5} alignItems='center' flexWrap='wrap-reverse'>
          <Text fontSize='4xl'>{name}</Text>
          <Tooltip label="Download PDF" fontSize="md">
            <IconButton variant='ghost' ml={5} isLoading={isDownloading} onClick={handleDownloadPdf}>
              <FaDownload size={20} />
            </IconButton>
          </Tooltip>
          {talent?.resume ? (
            <Tooltip label="Resumé" fontSize="md">
              <IconButton variant='ghost' onClick={() => {
                window.open(`${API_URL}/s3/${encodeURIComponent(talent?.resume)}`)
              }}>
                <FaFilePdf size={20} />
              </IconButton>
            </Tooltip>
            
          ) : null }
          {socialKeys?.map(key => {
            if (!talent.social_links || !talent.social_links[key]) { return null }
            return (
              <HStack key={key} position='relative' alignItems='end'>
                <Tooltip label={HelperText[key]} fontSize="md">
                  <IconButton variant='ghost' as={Link} href={talent.social_links[key]} target='_blank'>
                    {Icons[key]}
                  </IconButton>
                </Tooltip>
                <Text textAlign='center' position='absolute' left={-2} bottom={-2} fontSize={12}>{likesDisplay(talent.social_links[`${key}_followers`])}</Text>
              </HStack>
            )
          })}

          {(user && user.user_type === USER_TYPES.ADMIN) &&

          <Button ml={5} leftIcon={<FaCopy />} onClick={() => {
            setShowBookInfo(true)
          }}>
            Generate Booking Information
          </Button>

          }
          <Image
            ml='auto'
            height={50}
            src={PalomaLogo}
          />
         
        </Box>
        <Box display='flex' rowGap={5} columnGap={40} bg={darkColor} color={colors.primaryText} py={5} my={5} px={5} flexWrap='wrap'>
          <Box display='flex' gap={10} flexWrap='wrap'>
            <Box><Text>Gender </Text><Text fontWeight='bold'>{getOptionLabel(talent.gender, enumData.gender, true)}</Text></Box>
            <Box><Text>Hair Color </Text><Text fontWeight='bold'>{getOptionLabel(talent.hair_color, enumData.hair_color)}</Text></Box>
            <Box><Text>Eye Color </Text><Text fontWeight='bold'>{getOptionLabel(talent.eye_color, enumData.eye_color)}</Text></Box>
          </Box>
          <Box display='flex' gap={10} flexWrap='wrap'>
            <Box><Text>Clothing Size </Text><Text fontWeight='bold'>{getOptionLabel(talent.cloth_size, enumData.cloth_size)}</Text></Box>
            <Box><Text>Shoe Size </Text><Text fontWeight='bold'>{getOptionLabel(talent.shoe_size, enumData.shoe_size)}</Text></Box>
            <Box><Text>Height </Text><Text fontWeight='bold'>{getOptionLabel(talent.height, enumData.height)}</Text></Box>
            <Box><Text>Weight </Text><Text fontWeight='bold'>{getOptionLabel(talent.weight, enumData.weight)}</Text></Box>
          </Box>
          <Box display='flex' gap={10} flexWrap='wrap'>
            {/* {typeof talent.is_over_25 !== 'boolean' && ( */}
            {talentAge.asYears() < 18 && (
              <Box>
                <Text>DOB </Text>
                <Text fontWeight='bold' mr={4}>
                  {getDatePart(talent.birth_date)}
                </Text>
              </Box>
            )}
            <Box>
              {ageText && <Text>Age </Text>}
              <Text fontWeight='bold' >
                {ageText}
              </Text>
            </Box>
          </Box>
        </Box>

        <Box alignItems='flex-start' display='flex' gap={5} flexWrap='nowrap' overflowX='auto' flex={1} pb={5} minH='50vh'>
          {talent?.photos?.map((photo, index) => {
            return (
              <Image
                key={photo}
                src={`${API_URL}/s3/${encodeURIComponent(photo)}`}
                h='full'
                onClick={() => handlePhotoClick(index)}
                cursor="pointer"
              />
            )
          })}
        </Box>
      </Box>

      <Box ref={printRef} className='print-only'>
        <LightMode>
          <Box px={5} display='flex' mt={5} alignItems='center' flexWrap='wrap-reverse'>
            <Text color='black' fontSize='4xl'>{name}</Text>
            <IconButton variant='ghost' ml={5}>
              <FaFilePdf size={20} />
            </IconButton>
            {socialKeys?.map(key => {
              if (!talent.social_links || !talent.social_links[key]) { return null }
              return (
                <HStack key={key} position='relative' alignItems='end'>
                  <IconButton variant='ghost' as={Link} href={talent.social_links[key]} target='_blank'>
                    {Icons[key]}
                  </IconButton>
                  <Text textAlign='center' position='absolute' left={-2} bottom={-2} fontSize={12}>{likesDisplay(talent.social_links[`${key}_followers`])}</Text>
                </HStack>
              )
            })}
            <Image
              ml='auto'
              height={50}
              src={PalomaLogo}
            />
          </Box>
          <Box mx={5} display='flex' rowGap={5} columnGap={10} flexWrap='wrap'>
            <Box>
              <Text color='black'>Gender </Text>
              <Text color='black' fontWeight='bold'>{getOptionLabel(talent.gender, enumData.gender, true)}</Text>
            </Box>
            <Box>
              <Text color='black'>Hair Color </Text>
              <Text color='black' fontWeight='bold'>{getOptionLabel(talent.hair_color, enumData.hair_color)}</Text>
            </Box>
            <Box>
              <Text color='black'>Eye Color </Text>
              <Text color='black' fontWeight='bold'>{getOptionLabel(talent.eye_color, enumData.eye_color)}</Text>
            </Box>
            <Box>
              <Text color='black'>Clothing Size </Text>
              <Text color='black' fontWeight='bold'>{getOptionLabel(talent.cloth_size, enumData.cloth_size)}</Text>
            </Box>
            <Box>
              <Text color='black'>Shoe Size </Text>
              <Text color='black' fontWeight='bold'>{getOptionLabel(talent.shoe_size, enumData.shoe_size)}</Text>
            </Box>
            <Box>
              <Text color='black'>Height </Text>
              <Text color='black' fontWeight='bold'>{getOptionLabel(talent.height, enumData.height)}</Text>
            </Box>
            <Box>
              <Text color='black'>Weight </Text>
              <Text color='black' fontWeight='bold'>{getOptionLabel(talent.weight, enumData.weight)}</Text>
            </Box>
            <Box display='flex' gap={10} flexWrap='wrap'>
              {/* {typeof talent.is_over_25 !== 'boolean' && ( */}
              {talentAge.asYears() < 18 && (
                <Box>
                  <Text color='black'>DOB </Text>
                  <Text color='black' fontWeight='bold' mr={4}>
                    {getDatePart(talent.birth_date)}
                  </Text>
                </Box>
              )}
              <Box>
                {ageText && <Text color='black'>Age </Text>}
                <Text color='black' fontWeight='bold'>
                  {ageText}
                </Text>
              </Box>
            </Box>
          </Box>
          <Box mx={3} my={3} gap={3} display='grid' gridTemplateColumns='repeat(3, 1fr)'>
            {talent?.photos?.slice(0, 9)?.map(photo => {
              return (
                <Image
                  key={photo}
                  w='100%'
                  src={`${API_URL}/s3/${encodeURIComponent('300x300/' + photo)}`}
                  objectFit='contain'
                  style={{ pageBreakBefore: 'avoid' }}
                />
              )
            })}
          </Box>
        </LightMode>
      </Box>

      <Modal isOpen={showBookInfo} onClose={() => {
        setShowBookInfo(false)
      }} blockScrollOnMount={false}>
        <ModalOverlay />
        <ModalContent>
          <TalentBookInfo
            talent={talent}
            close={() => {
              setShowBookInfo(false)
            }}
          />
        </ModalContent>
      </Modal>
      <Modal isOpen={isPhotoModalOpen} onClose={handlePhotoCloseModal} size="">
        <ModalOverlay />
        <ModalContent maxWidth="40vw">
          <ModalCloseButton />
          <ModalBody>
            <Image
              src={`${API_URL}/s3/${encodeURIComponent(talent.photos[selectedPhotoIndex])}`}
              maxHeight="90vh"
              mx="auto"
            />
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  )
}

export default TalentView
